import { FormEvent, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { Input, Label, Textarea } from '@components/common/Forms';
import { Button, TailwindSpinner } from '@components/common';
import { useBjForm } from '@hooks/useBjForm';
import { useSwrJobInterviewQuestions } from '@hooks/useSwrApi/endpoints/job/useSwrJobInterviewQuestions';
import { useSwrJobInterviewSaveTextAnswerList } from '@hooks/useSwrApi/endpoints/job-interview/useSwrJobInterviewSaveTextAnswerList';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';

interface TextInterviewFormProps {
  jobSlug: string;
  locale: Locale;
  onSaveSuccess: () => void;
}

export const TextInterviewForm = (props: TextInterviewFormProps) => {
  const {
    jobSlug, locale, onSaveSuccess,
  } = props;

  const { t } = useTranslation('common');

  const {
    data: questionsData,
    isLoading: isFetchingQuestions,
    error: fetchError,
  } = useSwrJobInterviewQuestions({
    locale,
    // Without questionStatus, the backend returns every enabled question on
    // the job — including any that were already answered directly or filled
    // in by the phase-2 canonical-answer auto-cloner (`InterviewAnswerAutoCloner`).
    // The wizard should show only the ones the candidate still needs to
    // answer; UnifiedJobInterview passes the same filter for video/audio.
    payload: { filterJobSlugs: [jobSlug], questionStatus: 'unanswered' },
    shouldFetch: !!jobSlug,
  });

  const questions = questionsData?.items?.flatMap((item) => item.questions || []) || [];

  const {
    record, checkFormValidation,
  } = useBjForm({});

  const { trigger: saveTextAnswers, isMutating: isLoading } = useSwrJobInterviewSaveTextAnswerList({
    locale,
    successCallback: () => {
      sendGoogleTagEvent('interview_answer', 'Custom', { channel: 'text', subject: 'job' });
      onSaveSuccess();
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = questions.length > 0 ? checkFormValidation(event) : {};
    if (questions.length > 0 && (!formData || typeof formData !== 'object')) {
      return;
    }

    const textAnswers = questions.map((item, index) => {
      const textAnswerData = (formData as Record<string, { answerText: string; questionId: string }>)[`answers[${index}]`];
      return {
        questionId: Number(textAnswerData?.questionId || item.questionId),
        answerText: textAnswerData?.answerText || '',
      };
    });

    void saveTextAnswers({ textAnswers });
  };

  // Auto-proceed if no questions
  useEffect(() => {
    if (questionsData && questions.length === 0) {
      onSaveSuccess();
    }
  }, [onSaveSuccess, questions.length, questionsData]);

  // Loading state
  if (isFetchingQuestions || (!questionsData && !fetchError)) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <TailwindSpinner />
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="text-center">
        <p className="text-error">{t('apply.wizard.requirements.fetch.error')}</p>
      </div>
    );
  }

  // Render the form
  // **************************
  return (
    <div>
      <p className="pb-6 text-lg">
        {t('apply.wizard.mini_interview.subtitle')}
      </p>
      <form noValidate onSubmit={onSubmit}>

        {questions.map((item, index) => (
          <div key={item.questionId} className="mb-8">
            <Label
              htmlFor={`answers[${index}].answerText`}
              description={item.question}
              descriptionClass="text-md font-medium"
              required
            >
              <Textarea
                minLength={3}
                required
                {...record(`answers[${index}].answerText`)}
                placeholder={t('global.placeholder.answer')}
              />
              <Input type="hidden" {...record(`answers[${index}].questionId`)} value={item.questionId} />
            </Label>
          </div>
        ))}

        <div className="flex flex-col sm:items-start md:items-center">
          <Button
            type="submit"
            styling="solid"
            color="secondary"
            size="md"
            className="mb-2 w-full rounded-full md:mb-6 md:w-auto"
            isLoading={isLoading}
            disabled={isLoading}
          >
            {t('global.action.save')}
          </Button>
        </div>
      </form>
    </div>
  );
};
